import { Checkbox, Image } from '@heroui/react';
import { ColumnDef } from '@tanstack/react-table';
import React from 'react';
import ReactCountryFlag from 'react-country-flag';

import { countryFlagHelper } from '../../../config';
import { LanguageDataResponse } from '../../../types';
import { formatFirestoreDate } from '../../../utils/formatFirestoreDate';
import { ActionRow } from './Rows';

interface LanguagesColumnsProps {
  selectedIds: string[];
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;
  areAllSelected: boolean;
  isSomeSelected: boolean;
  onDelete: (language: LanguageDataResponse) => void;
  isDeleting: boolean;
}

export const getLanguagesColumns = ({
  selectedIds,
  onToggleRow,
  onToggleAll,
  areAllSelected,
  isSomeSelected,
  onDelete,
  isDeleting,
}: LanguagesColumnsProps): ColumnDef<LanguageDataResponse>[] => [
  {
    id: 'select',
    header: () => (
      <Checkbox
        size="sm"
        aria-label="Select all languages on this page"
        isSelected={areAllSelected}
        isIndeterminate={!areAllSelected && isSomeSelected}
        onValueChange={onToggleAll}
      />
    ),
    meta: { width: 'w-12' },
    cell: ({ row }) => (
      <Checkbox
        size="sm"
        aria-label={`Select ${row.original.name}`}
        isSelected={selectedIds.includes(row.original.id)}
        onValueChange={() => onToggleRow(row.original.id)}
      />
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'name',
    header: 'Name',
    meta: { width: 'w-[240px]' },
    cell: ({ row }) => {
      const { name, code } = row.original;
      const flag = countryFlagHelper(code);

      return (
        <div className="flex items-center gap-2">
          {flag.type === 'iso' ? (
            <ReactCountryFlag
              countryCode={flag.value}
              svg
              style={{ width: 18, height: 12 }}
            />
          ) : (
            <Image
              classNames={{ wrapper: 'inline-block' }}
              radius="none"
              src={flag.value}
              alt={flag.value}
              width={18}
              height={12}
            />
          )}
          <span>{name}</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'code',
    header: 'Code',
    meta: { width: 'w-[120px]' },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    meta: { width: 'w-[160px]' },
    // Wartością jest Timestamp (obiekt) — domyślny komparator dałby losową kolejność.
    enableSorting: false,
    // Języki dodane przed migracją na v3 nie mają `createdAt` → '-'.
    cell: ({ row }) => formatFirestoreDate(
      row.original.createdAt,
      { year: 'numeric', month: 'short', day: 'numeric' },
      'en-GB',
      '-',
    ),
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    // Ostatnia kolumna zabiera resztę szerokości — dzięki temu dane (przy 4 wąskich
    // kolumnach) trzymają się lewej, a akcje lądują przy prawej krawędzi tabeli.
    meta: { width: 'w-full', align: 'right' },
    cell: ({ row }) => (
      <ActionRow
        data={row.original}
        onDelete={onDelete}
        isDeleting={isDeleting}
      />
    ),
  },
];
